/**
 * アプリの外枠。
 *
 * 2026-07-29: モバイル対応。
 *   PC     … サイドバー常設（従来どおり）
 *   スマホ … サイドバーはドロワー（オーバーレイ）+ 下部タブバー
 *
 * ルート遷移でドロワーを自動的に閉じ、開いている間は背面のスクロールを止める。
 */
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { useMyPermissions } from '@/features/permissions';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/useIsMobile';

import {
  INTERNAL_NAV,
  MobileBottomNav,
  SA_NAV,
  type MobileNavItem,
} from './MobileBottomNav';
import { Sidebar } from './Sidebar';

import { hasPermission } from '@goenhub/shared';

export function AppLayout() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { user } = useAuth();
  const { isOwner, permissions } = useMyPermissions();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 画面遷移したらドロワーを閉じる
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  // PC に戻ったらドロワー状態をリセット
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  // ドロワー展開中は背面をスクロールさせない
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  // 外部 SA は見える世界が違うのでタブ構成を分ける
  const isExternalSa = user?.level === 'external';
  const navItems: MobileNavItem[] = (isExternalSa ? SA_NAV : INTERNAL_NAV).filter(
    (item) =>
      item.featureId === null ||
      hasPermission({
        isOwner,
        permissions,
        featureId: item.featureId,
        action: 'view',
      }),
  );

  return (
    <div className={`app-layout${isMobile ? ' is-mobile' : ''}`}>
      {isMobile && drawerOpen && (
        <div
          className="mobile-drawer-overlay"
          onClick={() => setDrawerOpen(false)}
          aria-hidden
        />
      )}
      {/* PC は従来どおり .app-layout(flex) の直下に .sidebar を置く。
          ラッパー div を挟むと flex アイテムが変わり PC のレイアウトが崩れるため、
          モバイルのときだけドロワー用のラッパーで包む。 */}
      {isMobile ? (
        <div className={`mobile-drawer${drawerOpen ? ' open' : ''}`}>
          <Sidebar />
        </div>
      ) : (
        <Sidebar />
      )}
      <main className="main-content">
        <Outlet />
      </main>
      {isMobile && (
        <MobileBottomNav items={navItems} onOpenMenu={() => setDrawerOpen(true)} />
      )}
    </div>
  );
}
